import React, { useEffect, useMemo, useState } from "react";
import styles from "./Warehouse.module.css";
import { API_URL } from "../../../config/api";

const Sales = ({ roleId, roleTitle }) => {
  const token = localStorage.getItem("token");

  const initialPermissions = useMemo(
    () => [
      { name: "Кассы", columns: ["view", "create", "edit", "delete"], key: "cashboxes" },
      { name: "Аккаунт кассового сервиса", columns: ["delete"], key: "cashier_account" },
      { name: "Показывать кассиру суммы продаж и средний чек", columns: ["view"], key: "show_sales" },
      { name: "Комментарии к продажам", columns: ["view", "create", "edit"], key: "sale_comments" },
      { name: "Акт выполненных работ", columns: ["view", "create", "delete"], key: "act_done" },
      { name: "Стоимость товара в режиме продажи", columns: ["edit"], key: "price_view" },
      { name: "Возврат", columns: ["view", "create"], key: "return" },
      { name: "Продажа", columns: ["view", "create", "edit"], key: "sale" },
      { name: "Скидка", columns: ["create"], key: "discount" },
      { name: "История продаж и возвратов в терминале", columns: ["view"], key: "sale_history" },
      { name: "Возврат поставщику", columns: ["view", "create"], key: "return_supplier" },
    ],
    []
  );

  const buildEmptyPermissions = () => {
    const empty = {};
    initialPermissions.forEach((p) => {
      empty[p.name] = { view: false, create: false, edit: false, delete: false };
    });
    return empty;
  };

  const [roleName, setRoleName] = useState("");
  const [terminalAccess, setTerminalAccess] = useState(false);
  const [permissions, setPermissions] = useState(() => buildEmptyPermissions());
  const [toast, setToast] = useState("");

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  useEffect(() => {
    // всегда делаем валидное состояние permissions, чтобы нигде не было undefined
    setPermissions(buildEmptyPermissions());

    if (!roleId) {
      setRoleName("");
      setTerminalAccess(false);
      return;
    }

    setRoleName(roleTitle || "");

    const fetchPermissions = async () => {
      try {
        const res = await fetch(`${API_URL}/api/roles/${roleId}/acls?category=sales`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        const permsObj = buildEmptyPermissions();

        initialPermissions.forEach((item) => {
          const perm = data?.data?.find((d) => d.key === item.key);

          permsObj[item.name] = {
            view: !!perm?.read,
            create: !!perm?.create,
            edit: !!perm?.update,
            delete: !!perm?.delete,
          };
        });

        setPermissions(permsObj);
      } catch (err) {
        console.error("Ошибка загрузки прав:", err);
      }
    };

    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId, roleTitle, token]);

  const handleCheckboxChange = (row, key) => {
    setPermissions((prev) => ({
      ...prev,
      [row]: {
        ...prev[row],
        [key]: !prev[row]?.[key],
      },
    }));
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      showToast("Введите название роли");
      return;
    }

    try {
      // обновление названия роли (если редактируем существующую)
      if (roleId && roleName !== roleTitle) {
        const updateRes = await fetch(`${API_URL}/api/roles/${roleId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ title: roleName }),
        });

        if (!updateRes.ok) {
          const err = await updateRes.json();
          showToast(err?.errors?.message || "Ошибка обновления названия!");
          return;
        }
      }

      const items = initialPermissions.map((item) => {
        const perms = permissions[item.name] ?? {
          view: false,
          create: false,
          edit: false,
          delete: false,
        };

        let type = 0;
        if (perms.view) type += 1;
        if (perms.create) type += 2;
        if (perms.edit) type += 4;
        if (perms.delete) type += 8;

        return {
          category: "sales",
          key: item.key,
          type,
          read: perms.view,
          create: perms.create,
          update: perms.edit,
          delete: perms.delete,
          id: null,
        };
      });

      let res;

      if (roleId) {
        res = await fetch(`${API_URL}/api/roles/${roleId}/acls/bulk_load`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ items }),
        });
      } else {
        res = await fetch(`${API_URL}/api/roles`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            only_terminal: terminalAccess,
            title: roleName,
            type: "cashier",
            items,
          }),
        });
      }

      const data = await res.json();

      if (!res.ok) {
        showToast(data?.errors?.message || "Ошибка сохранения!");
        return;
      }

      showToast(roleId ? "Роль обновлена!" : "Роль создана!");
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      showToast(err?.message || "Ошибка сохранения");
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <label>Название роли</label>

        <input
          type="text"
          className={styles.roleInput}
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          placeholder="Введите название роли"
        />

        <div className={styles.terminalWrapper}>
          <label className={styles.terminalLabel}>
            <label className={styles.checkboxContainer}>
              <input
                type="checkbox"
                checked={terminalAccess}
                onChange={() => setTerminalAccess((v) => !v)}
              />
              <span className={styles.customCheckbox}></span>
            </label>
            <span className={styles.text}>Доступ только в терминал</span>
          </label>
        </div>
      </div>

      <table className={styles.permissionTable}>
        <thead>
          <tr>
            <th>Название</th>
            <th>Просмотр</th>
            <th>Создать</th>
            <th>Изменить</th>
            <th>Удалить</th>
          </tr>
        </thead>

        <tbody>
          {initialPermissions.map((item) => (
            <tr key={item.name}>
              <td>{item.name}</td>

              {["view", "create", "edit", "delete"].map((col) => (
                <td key={col}>
                  {item.columns.includes(col) ? (
                    <label className={styles.checkboxContainer}>
                      <input
                        type="checkbox"
                        checked={permissions[item.name]?.[col] ?? false}
                        onChange={() => handleCheckboxChange(item.name, col)}
                      />
                      <span className={styles.customCheckbox}></span>
                    </label>
                  ) : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <button className={styles.saveButton} onClick={handleSave}>
        Сохранить
      </button>

      {toast && <div className={styles.toast}>{toast}</div>}
    </div>
  );
};

export default Sales;
